import { useEffect, useState } from "react";
import { Download, FileText, Leaf, Image as ImgIcon, BookOpen, Loader2 } from "lucide-react";
import api from "../lib/api";

const CATEGORY_META = {
  handout: { label: "Handout", Icon: FileText },
  rezept: { label: "Rezept", Icon: Leaf },
  flyer: { label: "Flyer", Icon: ImgIcon },
  poster: { label: "Poster", Icon: ImgIcon },
  anleitung: { label: "Anleitung", Icon: BookOpen },
};

function fmtBytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDownloads(n) {
  const v = Number(n) || 0;
  if (v === 0) return "Noch kein Download";
  if (v === 1) return "1 Download";
  if (v < 1000) return `${v} Downloads`;
  return `${(v / 1000).toFixed(1)}k Downloads`;
}

export default function Downloads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/downloads")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const onDownloadClick = (id) => {
    // Optimistic increment for instant UX
    setItems((prev) => prev.map((x) => x.id === id ? { ...x, download_count: (x.download_count || 0) + 1 } : x));
    // Fire-and-forget track call (no need to await; browser opens link in parallel)
    api.post(`/downloads/${id}/track`).catch(() => {});
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-8 py-12 md:py-16" data-testid="downloads-page">
      <div className="mb-10">
        <h1 className="font-display text-4xl md:text-5xl mb-3" style={{ color: "var(--wp-primary)" }}>
          Downloads
        </h1>
        <p className="text-base md:text-lg opacity-80 max-w-2xl">
          Kostenlose Handouts, Rezepte, Steckbriefe und Anleitungen zum Ausdrucken oder Mitnehmen auf deine nächste Wildkräuter-Wanderung.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 opacity-70">
          <Loader2 className="w-4 h-4 animate-spin" /> Lädt …
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 rounded-2xl bg-[var(--wp-cream)] text-center opacity-70">
          Aktuell sind keine Downloads verfügbar. Schau bald wieder vorbei.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => {
            const meta = CATEGORY_META[it.category] || CATEGORY_META.handout;
            const Icon = meta.Icon;
            const count = it.download_count || 0;
            const isPopular = count >= 10;
            return (
              <a
                key={it.id}
                href={it.file_url}
                target="_blank"
                rel="noopener noreferrer"
                download={it.file_name || "download.pdf"}
                onClick={() => onDownloadClick(it.id)}
                className="plant-card p-5 flex items-start gap-4 hover:shadow-lg transition-shadow relative"
                data-testid={`download-item-${it.id}`}
              >
                {isPopular && (
                  <span
                    className="absolute -top-2 -right-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold shadow-md"
                    style={{ backgroundColor: "var(--wp-primary)", color: "var(--wp-cream)" }}
                    data-testid={`download-popular-${it.id}`}
                  >
                    ★ Beliebt
                  </span>
                )}
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-primary)" }}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-xl truncate" style={{ color: "var(--wp-primary)" }}>{it.title}</h3>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--wp-cream)] opacity-70">{meta.label}</span>
                  </div>
                  {it.description && (
                    <p className="text-sm opacity-75 mt-1 line-clamp-3">{it.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs opacity-60 mt-3 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Download className="w-3.5 h-3.5" />
                      {it.file_name || "Download"}
                    </span>
                    {it.file_size ? <span>· {fmtBytes(it.file_size)}</span> : null}
                    <span
                      className="ml-auto text-[11px] font-medium px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-primary)" }}
                      data-testid={`download-count-${it.id}`}
                    >
                      {fmtDownloads(count)}
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
